// Story 5.3 — TanStack Query mutation hook for POST
// /api/v1/marketplace/listings/sealed-dossier.
//
// Sibling to Story 5.2's useCreateShareListing.ts. Same typed-error
// envelope shape (code / message / field / KYC threshold fields) so the
// consumer Dialog can branch on `code` regardless of which surface
// fired the mutation.
//
// Typed errors the consumer cares about:
//   * 400 VALIDATION_FAILED + field=price_cents | expires_in_days
//   * 402 KYC_REQUIRED + threshold_cents + current_cumulative_cents
//     + attempted_price_cents (Story 5.8 will upgrade to redirect)
//   * 409 LISTING_ALREADY_EXISTS — race / double-click (same wire-code
//     as the share-side sibling; only the message text differs)
//   * 401 — stale session; SPA should re-auth

import { useMutation, type UseMutationResult } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";

export type SealedDossierListingExpiresInDays = 7 | 30 | 90 | null;

export interface CreateSealedDossierListingVariables {
  sealed_dossier_id: string;
  price_cents: number;
  expires_in_days: SealedDossierListingExpiresInDays;
  seller_anonymous: boolean;
  /** Seller allows buyers to offer below the asking price. Optional on the
   *  wire (backend defaults false); the listing dialog always sets it. */
  allow_bidding?: boolean;
}

export interface CreateSealedDossierListingFeeBreakdown {
  platform_fee_cents: number;
  creator_royalty_cents: number;
  estimated_proceeds_cents: number;
}

export interface CreateSealedDossierListingResponseData {
  listing_id: string;
  sealed_dossier_id: string;
  price_cents: number;
  status: string;
  seller_anonymous: boolean;
  listed_at: string;
  expires_at: string | null;
  fee_breakdown: CreateSealedDossierListingFeeBreakdown;
}

export interface CreateSealedDossierListingError {
  code: string;
  message: string;
  field?: string;
  threshold_cents?: number;
  current_cumulative_cents?: number;
  attempted_price_cents?: number;
  status: number;
}

function freshIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return `idem-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

async function postCreateSealedDossierListing(
  variables: CreateSealedDossierListingVariables,
): Promise<CreateSealedDossierListingResponseData> {
  const response = await fetch("/api/v1/marketplace/listings/sealed-dossier", {
    method: "POST",
    credentials: "include",
    headers: {
      "content-type": "application/json",
      "x-idempotency-key": freshIdempotencyKey(),
    },
    body: JSON.stringify(variables),
  });
  attachRequestIdToScope(response);
  if (!response.ok) {
    let detail: CreateSealedDossierListingError = {
      code: "CREATE_LISTING_FAILED",
      message: `Listing creation failed (HTTP ${response.status})`,
      status: response.status,
    };
    try {
      const raw = await response.json();
      if (raw?.error) {
        detail = {
          code: raw.error.code,
          message: raw.error.message,
          field: raw.error.field,
          threshold_cents: raw.error.threshold_cents,
          current_cumulative_cents: raw.error.current_cumulative_cents,
          attempted_price_cents: raw.error.attempted_price_cents,
          status: response.status,
        };
      }
    } catch {
      // Body not JSON — keep default.
    }
    throw detail;
  }
  const body = (await response.json()) as {
    data: CreateSealedDossierListingResponseData;
  };
  return body.data;
}

export function useCreateSealedDossierListing(): UseMutationResult<
  CreateSealedDossierListingResponseData,
  CreateSealedDossierListingError,
  CreateSealedDossierListingVariables
> {
  return useMutation<
    CreateSealedDossierListingResponseData,
    CreateSealedDossierListingError,
    CreateSealedDossierListingVariables
  >({
    mutationFn: postCreateSealedDossierListing,
    // NO onSuccess — the Dialog (consumer) invalidates the vault-items
    // query because it knows the dialog was just used; trust comes from
    // the consumer surface, not the hook.
  });
}
